import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { PatentSearchResponse, PatentFilterContext, PatentDoc } from '../../apis/fto-pro-api';
import type { PatentInfo } from '../../types';
import type { PatentCacheService } from '../patent-cache-service';
import { SearchRoundsService } from '../search-rounds-service';
import { CurrentStep } from '../../enums';

/**
 * PatentDoc 到 PatentInfo 的转换器
 */
function convertPatentDocToPatentInfo(patentDoc: PatentDoc): PatentInfo {
    return {
        title: patentDoc.TITLE || '',
        abstract: '', // PatentDoc中没有abstract字段，暂时设为空字符串
        patentNumber: patentDoc.PN_STR || '',
        applicant: Array.isArray(patentDoc.ANCS) ? patentDoc.ANCS.join('; ') : '', // ANCS现在是数组，需要合并
        applicationDate: patentDoc.PBD || '', // 使用PBD字段作为日期
        similarity: patentDoc.score, // PatentDoc中没有similarity字段
        tags: [], // PatentDoc中没有tags字段，暂时设为空数组
        patentId: patentDoc.patent_id || '',
        selected: patentDoc.selected, // 传递selected属性
    };
}

/**
 * 专利缓存服务实现
 */
@injectable()
export class PatentCacheServiceImpl implements PatentCacheService {
    private _searchResults = new Map<number, PatentSearchResponse>();
    private _filterResults = new Map<string, PatentFilterContext>();

    constructor(
        @inject(SearchRoundsService)
        private readonly _searchRoundsService: SearchRoundsService,
    ) {}

    private _getFilterKey(searchIndex: number, filterIndex: number | CurrentStep): string {
        return `${searchIndex}-${filterIndex}`;
    }

    storeSearchResult(searchIndex: number, response: PatentSearchResponse): void {
        this._searchResults.set(searchIndex, response);
    }

    storeFilterResult(searchIndex: number, filterIndex: number, response: PatentFilterContext): void {
        const key = this._getFilterKey(searchIndex, filterIndex);
        this._filterResults.set(key, response);
    }

    // eslint-disable-next-line complexity
    getPatentData(searchIndex: number, filterIndex?: CurrentStep): PatentInfo[] {
        if (filterIndex !== undefined) {
            // 检查是否为人工输入且为最终步骤的特殊情况
            const searchRounds = this._searchRoundsService.getAllSearchRounds();
            const searchRound = searchRounds[searchIndex];

            if (searchRound?.isManual && filterIndex === CurrentStep.Final) {
                // 从搜索结果中获取数据，并过滤出 selected 为 true 的记录
                const searchResult = this._searchResults.get(searchIndex);
                if (searchResult?.patents) {
                    return searchResult.patents
                        .filter(patent => patent.selected === true)
                        .map(convertPatentDocToPatentInfo);
                }
                return [];
            }

            // 从过滤结果中获取数据
            const key = this._getFilterKey(searchIndex, filterIndex);
            const filterResult = this._filterResults.get(key);

            if (filterResult?.patents) {
                // 从过滤后的专利文档列表获取数据
                return filterResult.patents
                    .filter(patent => patent.remove_at_step < filterIndex)
                    .map(convertPatentDocToPatentInfo);
            }
            return [];
        } else {
            // 从搜索结果中获取数据
            const searchResult = this._searchResults.get(searchIndex);
            return searchResult?.patents.map(convertPatentDocToPatentInfo) || [];
        }
    }

    updatePatentSelection(searchIndex: number, patentId: string, selected: boolean): boolean {
        const searchResult = this._searchResults.get(searchIndex);
        if (!searchResult) {
            return false;
        }

        const patent = searchResult.patents.find(p => p.patent_id === patentId);
        if (!patent) {
            return false;
        }

        patent.selected = selected;
        return true;
    }

    clearSearchData(searchIndex: number): void {
        this._searchResults.delete(searchIndex);

        // 清除相关的过滤结果
        const keysToDelete: string[] = [];
        for (const key of this._filterResults.keys()) {
            if (key.startsWith(`${searchIndex}-`)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this._filterResults.delete(key));
    }

    clearAllData(): void {
        this._searchResults.clear();
        this._filterResults.clear();
    }
}
